import React, { useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { formatEUR } from "@/lib/api";
import { lineTotal } from "@/lib/escpos";

/**
 * Riga carrello con gesti swipe:
 * - swipe verso DESTRA → rimuovi riga
 * - swipe verso SINISTRA → +1 quantità
 * - tap → seleziona/deseleziona la riga (per sconto % o € sulla singola riga)
 */
export default function SwipeCartLine({ line, index, onRemove, onIncrement, onTap, selected = false, testidPrefix = "neg-cart" }) {
  const x = useMotionValue(0);
  const dragging = useRef(false);
  const removeOpacity = useTransform(x, [0, 40], [0, 1]);
  const plusOpacity = useTransform(x, [-40, 0], [1, 0]);

  const gross = (Number(line?.price) || 0) * (Number(line?.qty) || 0);
  const net = lineTotal(line);
  const hasDisc = net < gross - 0.001;
  const badge = line.discount_pct ? `−${line.discount_pct}%` : line.discount_eur ? `−${formatEUR(line.discount_eur)}` : null;

  return (
    <div className="relative overflow-hidden" data-testid={`${testidPrefix}-line-${index}`}>
      <motion.div style={{ opacity: removeOpacity }} className="absolute inset-0 bg-red-500 flex items-center px-3">
        <span className="text-white text-xs font-bold uppercase tracking-widest">✕ Rimuovi</span>
      </motion.div>
      <motion.div style={{ opacity: plusOpacity }} className="absolute inset-0 bg-[#2F9E77] flex items-center justify-end px-3">
        <span className="text-white text-xs font-bold uppercase tracking-widest">+1 unità</span>
      </motion.div>
      <motion.div
        drag="x"
        style={{ x }}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.6}
        onDragStart={() => { dragging.current = true; }}
        onDragEnd={(e, info) => {
          setTimeout(() => { dragging.current = false; }, 60);
          if (info.offset.x > 70) onRemove(index);
          else if (info.offset.x < -70) onIncrement(index);
        }}
        onClick={() => { if (!dragging.current) onTap?.(index); }}
        className={`relative flex items-center px-3 py-2.5 gap-2 touch-pan-y cursor-pointer ${
          selected ? "bg-blue-50 ring-2 ring-inset ring-[#1E2D5B]" : line.quick ? "bg-amber-50" : "bg-white"
        }`}
      >
        <span data-testid={`${testidPrefix}-qty-${index}`} className="font-mono font-bold text-sm w-9 text-center bg-velta-bg rounded py-0.5 shrink-0">
          {line.qty}×
        </span>
        <span className="flex-1 text-sm font-medium truncate">
          {line.name}
          {line.quick && <span className="ml-1 text-[9px] font-mono uppercase text-amber-700">(vendita rapida)</span>}
          {badge && (
            <span data-testid={`${testidPrefix}-disc-${index}`} className="ml-1.5 text-[9px] font-mono font-bold text-white bg-[#0F766E] rounded px-1 py-0.5">
              {badge}
            </span>
          )}
        </span>
        {hasDisc ? (
          <span className="text-right shrink-0">
            <span className="block text-[10px] font-mono line-through text-velta-mute leading-none">{formatEUR(gross)}</span>
            <span data-testid={`${testidPrefix}-amount-${index}`} className="font-mono font-bold text-sm text-[#0F766E]">{formatEUR(net)}</span>
          </span>
        ) : (
          <span data-testid={`${testidPrefix}-amount-${index}`} className="font-mono font-bold text-sm shrink-0">
            {formatEUR(gross)}
          </span>
        )}
      </motion.div>
    </div>
  );
}
